/**
 * session-recorder/SessionReplayerModal.tsx
 * 
 * Reprodutor Visual Interativo de Sessões de Testadores (DOM Activity Replay Engine).
 * Reproduz o cursor do mouse, cliques com efeito ripple, mudanças de rota, scroll e pausas.
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  FastForward, 
  Download, 
  X, 
  MapPin, 
  ShieldCheck, 
  AlertTriangle, 
  Monitor, 
  Smartphone, 
  MousePointer, 
  ExternalLink,
  Activity,
  Layers,
  Clock,
  User as UserIcon
} from 'lucide-react';
import type { RecordedSession, SessionEvent, MouseClickEvent, MouseMoveEvent, RouteChangeEvent, ErrorEvent } from './types';
import { exportSessionAsJson } from './sessionStorageService';
import { ReplayScreenCanvas } from './ReplayScreenCanvas';

interface SessionReplayerModalProps {
  session: RecordedSession | null;
  onClose: () => void;
}

export const SessionReplayerModal: React.FC<SessionReplayerModalProps> = ({ session, onClose }) => {
  if (!session) return null;

  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(2); // 1x, 2x, 4x
  const [currentTimeMs, setCurrentTimeMs] = useState<number>(0);
  const [currentRoute, setCurrentRoute] = useState<string>(session.initialRoute || '/');
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({ x: 100, y: 100 });
  const [activeClick, setActiveClick] = useState<MouseClickEvent | null>(null);
  const [recentEvents, setRecentEvents] = useState<SessionEvent[]>([]);
  const [isSkippingIdle, setIsSkippingIdle] = useState<boolean>(false);

  const totalDurationMs = Math.max(1000, session.durationSeconds * 1000);
  const animFrameRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(Date.now());

  // Reseta ao abrir nova sessão
  useEffect(() => {
    setCurrentTimeMs(0);
    setIsPlaying(true);
    setCurrentRoute(session.initialRoute || '/');
    lastTickRef.current = Date.now();
  }, [session.id]);

  // Loop de Reprodução
  useEffect(() => {
    if (!isPlaying) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }

    lastTickRef.current = Date.now();

    const tick = () => {
      const now = Date.now();
      const delta = (now - lastTickRef.current) * playbackSpeed;
      lastTickRef.current = now;

      setCurrentTimeMs((prev) => {
        const next = prev + delta;
        if (next >= totalDurationMs) {
          setIsPlaying(false);
          return totalDurationMs;
        }
        return next;
      });

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying, playbackSpeed, totalDurationMs]);

  // Atualiza os elementos visuais de acordo com o `currentTimeMs`
  useEffect(() => {
    const events = session.events;
    if (!events || events.length === 0) return;

    // Encontra a rota mais recente até este momento
    const pastRoutes = events.filter((e): e is RouteChangeEvent => e.type === 'route_change' && e.timestamp <= currentTimeMs);
    if (pastRoutes.length > 0) {
      setCurrentRoute(pastRoutes[pastRoutes.length - 1].path);
    }

    // Encontra a posição mais recente do cursor
    const pastMoves = events.filter((e): e is MouseMoveEvent | MouseClickEvent => 
      (e.type === 'mouse_move' || e.type === 'mouse_click') && e.timestamp <= currentTimeMs
    );
    if (pastMoves.length > 0) {
      const lastMove = pastMoves[pastMoves.length - 1];
      setCursorPos({ x: lastMove.x, y: lastMove.y });
    }

    // Verifica clique recente (nos últimos 400ms do replay)
    const pastClicks = events.filter((e): e is MouseClickEvent => e.type === 'mouse_click' && Math.abs(e.timestamp - currentTimeMs) < 350);
    if (pastClicks.length > 0) {
      setActiveClick(pastClicks[pastClicks.length - 1]);
    } else {
      setActiveClick(null);
    }

    // Verifica se está em período de ociosidade pausada
    const currentIdle = events.find((e) => e.type === 'pause_idle' && e.timestamp <= currentTimeMs && currentTimeMs <= e.timestamp + (e.idleDurationMs || 3000));
    setIsSkippingIdle(!!currentIdle);

    // Lista os últimos 5 eventos passados
    const pastEvents = events.filter((e) => e.timestamp <= currentTimeMs).slice(-6);
    setRecentEvents(pastEvents);
  }, [currentTimeMs, session.events]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMs = Number(e.target.value);
    setCurrentTimeMs(newMs);
  };

  const formatMs = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Coordenadas normalizadas para viewport mockup
  const screenW = session.device.viewportWidth || 1280;
  const screenH = session.device.viewportHeight || 800;
  const clampedX = Math.min(100, Math.max(0, (cursorPos.x / screenW) * 100));
  const clampedY = Math.min(100, Math.max(0, (cursorPos.y / screenH) * 100));

  const mapUrl = session.location 
    ? `https://www.google.com/maps?q=${session.location.latitude},${session.location.longitude}`
    : null;

  return (
    <div className="ac-replayer-backdrop">
      <div className="ac-replayer-modal">
        {/* CABEÇALHO DO REPLAYER */}
        <div className="ac-replayer-header">
          <div className="ac-replayer-header-left">
            <div className="ac-replayer-avatar">
              <UserIcon size={18} />
            </div>
            <div>
              <div className="ac-replayer-user-name">
                {session.user.nome} 
                <span className="ac-replayer-badge-role">{session.user.perfil}</span>
                {session.user.cargo && <span className="ac-replayer-badge-cargo">{session.user.cargo}</span>}
              </div>
              <div className="ac-replayer-meta-row">
                <span className="ac-replayer-meta-item">
                  📧 {session.user.email}
                </span>
                <span className="ac-replayer-meta-item">
                  📱 {session.device.deviceType === 'mobile' ? <Smartphone size={13} /> : <Monitor size={13} />} {session.device.browser} ({session.device.os})
                </span>
                {session.location && (
                  <a href={mapUrl!} target="_blank" rel="noreferrer" className="ac-replayer-meta-item ac-replayer-link">
                    <MapPin size={13} /> {session.location.city ? `${session.location.city}, ` : ''}{session.location.latitude.toFixed(4)}, {session.location.longitude.toFixed(4)}
                    <ExternalLink size={11} style={{ marginLeft: 3 }} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="ac-replayer-header-right">
            <div className={`ac-replayer-version-tag ${session.appVersion.isStaleAgainstCdn ? 'ac-replayer-version--stale' : 'ac-replayer-version--ok'}`}>
              {session.appVersion.isStaleAgainstCdn ? <AlertTriangle size={14} /> : <ShieldCheck size={14} />}
              <span>{session.appVersion.version} ({session.appVersion.gitCommit.substring(0, 7)})</span>
            </div>

            <button 
              onClick={() => exportSessionAsJson(session)} 
              className="ac-replayer-btn-secondary" 
              title="Baixar JSON da Sessão para Auditoria"
            >
              <Download size={15} />
              JSON
            </button>

            <button onClick={onClose} className="ac-replayer-btn-close" title="Fechar Replay">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ÁREA PRINCIPAL: NAVEGADOR MOCKUP COM CURSOR ANIMADO */}
        <div className="ac-replayer-body">
          {/* Barra de Endereço Simulada */}
          <div className="ac-replayer-browser-bar">
            <div className="ac-replayer-traffic-lights">
              <span className="ac-dot red" />
              <span className="ac-dot yellow" />
              <span className="ac-dot green" />
            </div>
            <div className="ac-replayer-url-input">
              <span className="ac-replayer-url-lock">🔒 https://alunoconectado.com.br</span>
              <span className="ac-replayer-url-path">{currentRoute}</span>
            </div>
            {isSkippingIdle && (
              <div className="ac-replayer-idle-badge">
                <Clock size={12} />
                Pausando 3s ocioso
              </div>
            )}
          </div>

          {/* Área de Visualização do Viewport com Renderizador de Alta Fidelidade */}
          <div className="ac-replayer-viewport">
            <ReplayScreenCanvas
              session={session}
              currentRoute={currentRoute}
              cursorPos={cursorPos}
              activeClick={activeClick}
              recentEvents={recentEvents}
              currentTimeMs={currentTimeMs}
            />
          </div>
        </div>

        {/* CONTROLES DE REPRODUÇÃO & TIMELINE */}
        <div className="ac-replayer-controls">
          <div className="ac-replayer-controls-left">
            <button 
              onClick={() => setIsPlaying(!isPlaying)} 
              className="ac-replayer-btn-play"
              title={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>

            <button 
              onClick={() => { setCurrentTimeMs(0); setIsPlaying(true); }} 
              className="ac-replayer-btn-icon"
              title="Reiniciar"
            >
              <RotateCcw size={15} />
            </button>

            <div className="ac-replayer-speed-selector">
              {[1, 2, 4].map((s) => (
                <button
                  key={s}
                  onClick={() => setPlaybackSpeed(s)}
                  className={`ac-replayer-speed-btn ${playbackSpeed === s ? 'active' : ''}`}
                >
                  {s}x
                </button>
              ))}
            </div>

            <div className="ac-replayer-time-display">
              {formatMs(currentTimeMs)} / {formatMs(totalDurationMs)}
            </div>
          </div>

          {/* BARRA DE PROGRESSO & TIMELINE COM MARCADORES */}
          <div className="ac-replayer-timeline-wrapper">
            <input
              type="range"
              min={0}
              max={totalDurationMs}
              value={currentTimeMs}
              onChange={handleSeek}
              className="ac-replayer-timeline-slider"
            />
            
            {/* Marcadores de cliques na timeline */}
            <div className="ac-replayer-timeline-markers">
              {session.events
                .filter((e) => e.type === 'mouse_click' || e.type === 'route_change' || e.type === 'error')
                .map((ev, idx) => {
                  const pct = (ev.timestamp / totalDurationMs) * 100;
                  const color = ev.type === 'error' ? '#ef4444' : (ev.type === 'route_change' ? '#3b82f6' : '#10b981');
                  return (
                    <span 
                      key={idx} 
                      className="ac-replayer-timeline-marker"
                      style={{ left: `${pct}%`, backgroundColor: color }}
                      title={`${ev.type} aos ${formatMs(ev.timestamp)}`}
                    />
                  );
                })}
            </div>
          </div>

          <div className="ac-replayer-stats-pill">
            <span>👆 {session.totalClicks} cliques</span>
            <span>⏱️ {session.activeSeconds}s ativos</span>
            <span>📊 {session.visitedRoutes.length} telas</span>
          </div>
        </div>
      </div>
    </div>
  );
};
